
import { computed, defineComponent, PropType, ref, watch, watchEffect } from 'vue';
import Modal from '@/components/Modal.vue';
import RangeInput from '@/components/RangeInput.vue';
import TimePicker from '@/components/TimePicker.vue';
import { DrugTarget } from '@/service/models/request/computation/drug-target';
import { Target } from '@/service/models/response/drug-model-detail/target/target';
import { useRequestDataStore } from '@/store/request-data';
import { deepCopy } from '@/utilities/cloning';

export default defineComponent({
    components: {
        Modal,
        RangeInput,
        TimePicker,
    },

    props: {
        isOpen: {
            type: Boolean,
            required: true,
        },

        index: {
            type: Number,
            required: true,
        },

        data: {
            type: null as unknown as PropType<DrugTarget|null>,
            required: true,
        },
    },

    emits: {
        "update:isOpen": (payload: boolean) => typeof payload === "boolean",
        "save": (payload: {index: number, data: DrugTarget}) => typeof payload === "object",
    },

    setup(props, {emit})
    {
        const requestData = useRequestDataStore();
        const drugActiveMoiety = requestData.selectedDrugModel?.model.activeMoieties[0] ?? null;
        const internalData = ref(requestData.makeTarget(
            drugActiveMoiety,
            drugActiveMoiety?.targets[requestData.targets.length] ?? null
        ));
        const enableAlarms = ref(false);

        const availableTypes = Target.Type;

        const valuesUnit = computed<string>(() => {
            return Target.TypeToUnitMapping.get(internalData.value.type) ?? "";
        });

        const typeHasMic = computed<boolean>(() => {
            return (
                internalData.value.type === Target.Type.AUC_DIVIDED_BY_MIC ||
                internalData.value.type === Target.Type.AUC_24_DIVIDED_BY_MIC ||
                internalData.value.type === Target.Type.AUC_OVER_MIC ||
                internalData.value.type === Target.Type.AUC_24_OVER_MIC ||
                internalData.value.type === Target.Type.PEAK_DIVIDED_BY_MIC ||
                internalData.value.type === Target.Type.TIME_OVER_MIC ||
                internalData.value.type === Target.Type.RESIDUAL_DIVIDED_BY_MIC
            );
        });

        const typeHasTimes = computed<boolean>(() => {
            return internalData.value.type === Target.Type.PEAK;
        });

        // Need to update some values in the model when the type is modified
        watchEffect(() => {
            internalData.value.unit = valuesUnit.value;
        });
        watchEffect(() => {
            if (!typeHasMic.value) {
                internalData.value.micUnit = null;
                internalData.value.micValue = null;
            }

            if (!typeHasTimes.value) {
                internalData.value.tMin = null;
                internalData.value.tBest = null;
                internalData.value.tMax = null;
            }
        });
        watchEffect(() => {
            if (!enableAlarms.value) {
                internalData.value.inefficacyAlarm = null;
                internalData.value.toxicityAlarm = null;
            }
        });

        function refreshInternalData(newData: DrugTarget|null)
        {
            if (newData !== null) {
                // Needs to perform a deep copy of the object because we don't want the passed object
                // to be modified until the user clicks the "save" button.
                internalData.value = deepCopy(newData);
            }
            else {
                // Create a new target
                internalData.value = requestData.makeTarget(drugActiveMoiety, null);
            }

            enableAlarms.value = (internalData.value.inefficacyAlarm !== null || internalData.value.toxicityAlarm !== null);
        }

        // The internal data object needs to be copied either when the parent component changes it
        // or each time the modal is re-opened (for example to create multiple new items).
        refreshInternalData(props.data);
        watch(
            [() => props.data, () => props.isOpen],
            () => refreshInternalData(props.data)
        );

        function handleClose()
        {
            emit("update:isOpen", false);
        }

        function handleOk()
        {
            // Alsow needs to perform a copy of the internal value before returning it,
            // to prevent it from leaking into the parent components.
            emit("save", {
                index: props.index,
                data: deepCopy(internalData.value),
            });

            handleClose();
        }

        return {
            internalData,
            enableAlarms,
            availableTypes,
            valuesUnit,
            typeHasMic,
            typeHasTimes,
            handleClose,
            handleOk,
        };
    }
});
